import {
  Component,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DocumentService,
  EmailDocumentService,
} from '@maximizer/core/shared/data-access';
import {
  EmailDocument,
  ListItem,
  SecurityAccessListItem,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  nullIfBlank,
  OutlookService,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryType,
  EmailEntryType,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OfficeItem,
  OutlookEmail,
  OutlookEmailDetail,
  OutlookEmailDetailEvent,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, firstValueFrom, of } from 'rxjs';

interface EmailDocumentForm {
  name: string;
  fullAccess: string;
  readAccess: string;
  category: string | null;
  result: string | null;
  attachment: boolean;
}

@Component({
  selector: 'maximizer-email-detail',
  templateUrl: './email-detail.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIP,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class EmailDetailComponent implements OnInit {
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  closed = output<void>();
  saveEmail = output<OutlookEmail[]>();
  saveEmailKey = output<OutlookEmailDetailEvent>();

  emailProperties = input<OutlookEmailDetail | undefined>(undefined);

  // amplitude event tracking variables
  saveToAll = input(false);
  saveFromPopup = input(false);
  entityTypeOverride = input<EntryType | undefined>(undefined);

  // internal tracking entity type that can be updated
  internalTrackerEntityType = signal<EntryType | AbEntryType | EmailEntryType>(
    EntryType.Contact,
  );

  loading = false;
  form: FormGroupWrapper<EmailDocumentForm>;
  category: ListItem<string>[] = [];
  result: ListItem<string>[] = [];
  readAccess: SecurityAccessListItem[] = [];
  fullAccess: SecurityAccessListItem[] = [];
  emlFile: File | null = null;
  mailboxItem: OfficeItem | null = null;

  constructor(
    private readonly emailDocumentService: EmailDocumentService,
    private readonly documentService: DocumentService,
    private readonly outlookService: OutlookService,
    private readonly insightsService: InsightsService,
    private readonly tracker: TrackerService,
    private readonly translateService: TranslateService,
  ) {
    this.form = FormWrapperBuilder.group<EmailDocumentForm>({
      name: [null, [Validators.required, Validators.maxLength(255)]],
      fullAccess: [null, [Validators.required]],
      readAccess: [null, [Validators.required]],
      category: [null],
      result: [null],
      attachment: [true],
    });
  }

  ngOnInit(): void {
    this.loading = true;

    const entityTypeOverride = this.entityTypeOverride();
    if (entityTypeOverride) {
      this.internalTrackerEntityType.set(entityTypeOverride);
    }

    this.emailDocumentService
      .getFieldOptions()
      .pipe(
        catchError((error) => {
          this.handleError(error, 'Error fetching field options');

          return of({
            category: [],
            result: [],
            fullAccess: [],
            readAccess: [],
          });
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((data) => {
        this.category = data.category;
        this.result = data.result;
        this.fullAccess = data.fullAccess;
        this.readAccess = data.readAccess;

        const emailPropertiesValue = this.emailProperties();
        this.form.group.patchValue({
          fullAccess: this.fullAccess[0]?.id ?? '*',
          readAccess: this.readAccess[0]?.id ?? '*',
          name:
            nullIfBlank(emailPropertiesValue?.subject) ??
            this.translateService.instant('outlook.email.noSubject'),
        });

        const parentKeys = emailPropertiesValue
          ? this.getParentKeysFromEmailDetail(emailPropertiesValue)
          : [];
        if (parentKeys.length === 1) {
          this.setEmailDocumentDefaults(parentKeys);
        }
      });

    this.checkAttachmentPreferenceFromStorage();
  }

  setEmailDocumentDefaults(keys: string[]): void {
    const decoded = atob(keys[0]);
    const firstToken = decoded.split('\t')[0];
    let type = firstToken.toLowerCase();

    // Map CScase to case for options compatibility
    if (type === 'cscase') {
      type = 'case';
    }

    const entryType = type;

    this.emailDocumentService
      .readEmailDefaultByUserAndType(entryType)
      .subscribe((defaults) => {
        if (!defaults) return;

        this.form.group.patchValue({
          result: defaults.result,
          category: defaults.category,
          fullAccess: defaults.fullAccess ?? this.form.value.fullAccess,
          readAccess: defaults.readAccess ?? this.form.value.readAccess,
        });
      });
  }

  checkAttachmentPreferenceFromStorage(): void {
    const attachmentPreference = localStorage.getItem('saveEmailAttachment');
    if (attachmentPreference !== null) {
      this.form.group.patchValue({
        attachment: attachmentPreference === 'true',
      });
    }
  }

  setSaveAttachmentPreference(saveAttachment: boolean): void {
    localStorage.setItem('saveEmailAttachment', String(saveAttachment));
  }

  async save(): Promise<void> {
    const emailPropertiesValue = this.emailProperties();
    if (!emailPropertiesValue || !this.form.valid) {
      this.notification().showMessage(
        'error',
        'outlook.email.form.error',
        true,
      );
      return;
    }

    this.loading = true;
    let isSuccessful = false;
    let successfulSaves: { parentKey: string; documentId: string }[] = [];

    try {
      this.setSaveAttachmentPreference(this.form.value.attachment);
      this.mailboxItem = this.outlookService.mailboxItem;

      const emailDetail = emailPropertiesValue;
      const entries = this.getEntriesFromEmailDetail(emailDetail);
      const parentKeys = this.getParentKeysFromEmailDetail(emailDetail);

      this.emlFile ??= await this.generateEmlFile();

      if (!this.emlFile) {
        this.notification().showMessage(
          'error',
          'outlook.email.form.error',
          true,
        );
        return;
      }

      const attachmentId = await firstValueFrom(
        this.documentService.createAttachment(
          this.emlFile,
          'application/octet-stream',
        ),
      );

      if (!attachmentId) {
        this.handleError('Missing Attachment Id', 'Error saving attachment');
        return;
      }

      successfulSaves = await this.performSaves(
        parentKeys,
        attachmentId,
        emailDetail,
      );
      isSuccessful = successfulSaves.length === parentKeys.length;

      this.handleSuccessfulSave(
        emailDetail,
        entries,
        parentKeys,
        successfulSaves,
      );
    } catch (error) {
      this.handleError(error, 'Unexpected error in save email detail');
    } finally {
      this.emlFile = null;
      this.loading = false;

      if (this.saveToAll()) {
        this.trackSaveAllCompletedEvent(isSuccessful);
      }
    }
  }

  private async performSaves(
    parentKeys: string[],
    attachmentId: string,
    emailDetail: OutlookEmailDetail,
  ): Promise<{ parentKey: string; documentId: string }[]> {
    const successfulSaves: { parentKey: string; documentId: string }[] = [];
    const saveAll = this.saveToAll();

    for (let i = 0; i < parentKeys.length; i++) {
      const parentKey = parentKeys[i];
      const isLast = i === parentKeys.length - 1;

      const document = this.buildEmailDocument(parentKey, attachmentId);
      const documentId = await firstValueFrom(
        this.emailDocumentService.create(document, isLast),
      );

      this.updateTrackerEntityType(emailDetail, parentKey);

      if (documentId) {
        successfulSaves.push({ parentKey, documentId });

        if (!saveAll) {
          this.trackSaveCompletedEvent(true);
        }
      } else if (!saveAll) {
        this.trackSaveCompletedEvent(false);
      }
    }

    return successfulSaves;
  }

  private updateTrackerEntityType(
    emailDetail: OutlookEmailDetail,
    parentKey: string,
  ): void {
    if (this.entityTypeOverride()) {
      return;
    }

    if ('type' in emailDetail) {
      if (
        (emailDetail.type === EntryType.AbEntry ||
          emailDetail.type === EntryType.AddressBook) &&
        emailDetail.abentryType
      ) {
        this.internalTrackerEntityType.set(emailDetail.abentryType);
      } else {
        this.internalTrackerEntityType.set(emailDetail.type);
      }
      return;
    }

    if ('entry' in emailDetail) {
      const entries = this.getEntriesFromEmailDetail(emailDetail);
      const entry = entries.find((e) => e.id === parentKey);
      if (!entry?.type) {
        return;
      }
      if (entry.type === EntryType.AbEntry && entry.abentryType) {
        this.internalTrackerEntityType.set(entry.abentryType);
      } else {
        this.internalTrackerEntityType.set(entry.type);
      }
    }
  }

  private handleSuccessfulSave(
    emailDetail: OutlookEmailDetail,
    entries: OutlookEmail[],
    parentKeys: string[],
    successfulSaves: { parentKey: string; documentId: string }[],
  ): void {
    if ('entry' in emailDetail) {
      const updatedEntries = entries.map((entry) => {
        const saved = successfulSaves.find(
          (email) => email.parentKey === entry.id,
        );
        return {
          ...entry,
          emailSaved: !!saved,
          emailKey: saved?.documentId,
        };
      });
      this.saveEmail.emit(updatedEntries);
    } else if ('type' in emailDetail) {
      this.saveEmailKey.emit({
        id: parentKeys[0],
        type: emailDetail.type,
      });
    }
  }

  private async generateEmlFile(): Promise<File | null> {
    try {
      if (this.emlFile) return this.emlFile;

      const isCompose = this.outlookService.isCompose;
      const removeAttachment = !this.form.value.attachment;

      if (isCompose) {
        return await this.outlookService.getSavedComposeEmlFile(
          removeAttachment,
        );
      } else {
        return await this.outlookService.getReadEmlFile(removeAttachment);
      }
    } catch (error) {
      this.handleError(error, 'Error generating EML file for email details');
      return null;
    }
  }

  private getEntriesFromEmailDetail(
    emailDetail: OutlookEmailDetail,
  ): OutlookEmail[] {
    if ('entry' in emailDetail) {
      return Array.isArray(emailDetail.entry)
        ? emailDetail.entry
        : [emailDetail.entry];
    }
    return [];
  }

  private getParentKeysFromEmailDetail(
    emailDetail: OutlookEmailDetail,
  ): string[] {
    if ('entry' in emailDetail) {
      return this.getEntriesFromEmailDetail(emailDetail)
        .map((entry) => entry.id)
        .filter((id): id is string => typeof id === 'string' && !!id);
    }

    if ('key' in emailDetail && emailDetail.key) {
      return [emailDetail.key];
    }

    return [];
  }

  private buildEmailDocument(
    parentKey: string,
    attachmentId: string,
  ): EmailDocument {
    return {
      id: null,
      name: this.form.value.name,
      binaryDataId: attachmentId,
      dateTime: this.mailboxItem?.dateTimeCreated ?? new Date(),
      size: this.emlFile?.size ?? 0,
      parentKey,
      result: this.form.value.result,
      category: this.form.value.category,
      fullAccess: this.form.value.fullAccess,
      readAccess: this.form.value.readAccess,
    };
  }

  private trackSaveCompletedEvent(success: boolean): void {
    this.tracker.trackEvent({
      module: EventModule.Email,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
        entity: this.internalTrackerEntityType(),
        emailDetails: true,
        search: this.saveFromPopup(),
      },
    });
  }

  private trackSaveAllCompletedEvent(success: boolean): void {
    this.tracker.trackEvent({
      module: EventModule.Email,
      journey: EventJourney.SaveToAll,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
        emailDetails: true,
      },
    });
  }

  private handleError(error: unknown, context: string): void {
    const isError = error instanceof Error;
    const exception = isError ? error : new Error('Unknown error');

    this.insightsService.trackException(
      { exception },
      {
        context,
        message: isError
          ? `${context}: ${exception.message}`
          : `${context}: Non-error thrown: ${JSON.stringify(error)}`,
      },
    );

    this.notification().showMessage('error', 'outlook.email.form.error', true);
  }
}
